<template>
  <div class="w-full h-full grid grid-rows-[auto_1fr] overflow-hidden">
    <ThreadsNav :show-back="true">
      <template #center>{{ $t("Dungeons.createRoom") }}</template>
    </ThreadsNav>
    <div class="overflow-auto">
      <VanCellGroup inset>
        <VanField
          :label="$t('Dungeons.create.name')"
          label-width="100px"
          v-model="room.name"
          :name="$t('Dungeons.create.name')"
          :placeholder="$t('Dungeons.create.rules.name')"
          :rules="[
            { required: true, message: $t('Dungeons.create.rules.name') },
          ]"
          required
        />
        <VanField
          :model-value="sceneOptionsMap[room.chatSceneId]"
          :label="$t('Dungeons.create.scene')"
          :name="$t('Dungeons.create.scene')"
          :placeholder="$t('Dungeons.create.rules.scene')"
          :rules="[
            { required: true, message: $t('Dungeons.create.rules.scene') },
          ]"
          is-link
          readonly
          required
          @click="showScenePicker = true"
        />
        <VanField
          :model-value="room.mode"
          :label="$t('Dungeons.create.mode')"
          :name="$t('Dungeons.create.mode')"
          :placeholder="$t('Dungeons.create.rules.mode')"
          :rules="[
            { required: true, message: $t('Dungeons.create.rules.mode') },
          ]"
          is-link
          readonly
          required
          @click="showModePicker = true"
        />
        <VanField
          :model-value="room.maxPlayers"
          :label="$t('Dungeons.create.maxMate')"
          :name="$t('Dungeons.create.maxMate')"
          :placeholder="$t('Dungeons.create.rules.maxMate')"
          :rules="[
            { required: true, message: $t('Dungeons.create.rules.maxMate') },
          ]"
          is-link
          readonly
          required
          @click="showMaxMatePicker = true"
        />
        <VanField
          :label="$t('Dungeons.create.duration')"
          :model-value="room.duration"
          :name="$t('Dungeons.create.duration')"
          :placeholder="$t('Dungeons.create.rules.duration')"
          type="digit"
          :rules="[
            { required: true, message: $t('Dungeons.create.rules.duration') },
          ]"
          is-link
          readonly
          required
          @click="showDurationPicker = true"
        />

        <VanField
          :label="$t('Dungeons.create.shortDefinition')"
          v-model="room.shortDefinition"
          :name="$t('Dungeons.create.shortDefinition')"
          :placeholder="$t('Dungeons.create.rules.shortDefinition')"
          autosize
          label-align="top"
          type="textarea"
          show-word-limit
          :maxlength="80"
        />
      </VanCellGroup>
      <VanCellGroup class="my-2" inset>
        <VanField
          :label="$t('Dungeons.create.startDate')"
          :model-value="moment(room.dtStart).format('YYYY-MM-DD')"
          :name="$t('Dungeons.create.startDate')"
          :placeholder="$t('Dungeons.create.rules.startDate')"
          :rules="[
            { required: true, message: $t('Dungeons.create.rules.startDate') },
          ]"
          is-link
          readonly
          required
          @click="showStartDatePicker = true"
        />
        <VanField
          :label="$t('Dungeons.create.startTime')"
          :model-value="moment(room.dtStart).format('HH:mm')"
          :name="$t('Dungeons.create.startTime')"
          :placeholder="$t('Dungeons.create.rules.startTime')"
          :rules="[
            { required: true, message: $t('Dungeons.create.rules.startTime') },
          ]"
          is-link
          readonly
          required
          @click="showStartTimePicker = true"
        />

        <VanCell>
          <template #title>
            <p class="text-sm text-base-content/50">
              {{ $t("Dungeons.create.startModeDesc") }}
            </p>
          </template>
          <template #label>
            <VanRadioGroup v-model="room.startMode" :icon-size="14" shape="dot">
              <VanRadio v-for="i in 3" :key="i" :name="i" class="mb-1">
                {{ $t(`Dungeons.create.startModeOption.${i}`) }}
              </VanRadio>
            </VanRadioGroup>
          </template>
        </VanCell>
        <!-- <p class="divider my-0"></p>
        <p class="text-sm text-base-content/50">
          {{ $t("Dungeons.create.startTimeTips") }}
        </p> -->
      </VanCellGroup>

      <VanCellGroup class="my-2" inset>
        <VanCell>
          <template #title>
            <p class="text-sm text-base-content/50">
              {{ $t("Dungeons.create.paymentModeDesc") }}
            </p>
          </template>
          <template #label>
            <VanRadioGroup
              v-model="room.paymentMode"
              :icon-size="14"
              shape="dot"
            >
              <VanRadio v-for="i in 2" :key="i" :name="i" class="mb-1">
                {{ $t(`Dungeons.create.paymentModeOption.${i}`) }}
              </VanRadio>
            </VanRadioGroup>
          </template>
        </VanCell>
      </VanCellGroup>

      <div class="text-center mb-4">
        <button
          class="btn btn-primary btn-wide py-2 rounded-md"
          @click="clickCreateRoom"
        >
          <span>{{ $t("Dungeons.createRoom") }}</span>
          <span>10</span>
          <CrystalIcon />
        </button>
      </div>
    </div>
    <ModalSelectOption
      v-model:show="showScenePicker"
      :options="sceneOptions"
      @confirm="onSceneSelected"
    />
    <ModalSelectOption
      v-model:show="showModePicker"
      :options="modeOptions"
      @confirm="onModeSelected"
    />
    <ModalSelectOption
      v-model:show="showMaxMatePicker"
      :options="maxMateOptions"
      @confirm="onMaxMateSelected"
    />
    <ModalSelectOption
      v-model:show="showDurationPicker"
      :options="durationOptions"
      @confirm="onDurationSelected"
    />
    <VanPopup v-model:show="showStartDatePicker" position="bottom">
      <VanDatePicker
        v-model="startDate"
        type="date"
        @confirm="onStartDateSelected"
      />
    </VanPopup>
    <VanPopup v-model:show="showStartTimePicker" position="bottom">
      <VanTimePicker
        v-model="startTime"
        type="time"
        @confirm="onStartTimeSelected"
      />
    </VanPopup>
  </div>
</template>

<script setup lang="ts">
import { ScriptGameRoom } from "@/types/game";
import { useMate } from "@/stores/mate";
import { computed, ref } from "vue";
import moment from "moment";
import { createScriptGameRoom } from "@/apis/script-game";
import { useI18n } from "vue-i18n";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";
import { useRouter } from "vue-router";
const { t } = useI18n();
const mateStore = useMate();
const now = new Date();
const room = ref<ScriptGameRoom>({
  chatSceneId: 1,
  maxPlayers: 2,
  duration: 10,
  mode: 1,
  dtStart: now.toISOString(),
  startMode: 1,
  paymentMode: 1,
} as ScriptGameRoom);

const router = useRouter();
const mateList = computed(() => {
  return Array.from(mateStore.mateMap.values()).slice(0, 10) || [];
});

// Scene Picker
const showScenePicker = ref(false);
const sceneOptions = [
  {
    text: "School Days",
    value: 1,
  },
];
const sceneOptionsMap: any = {
  1: "School Days",
};
const onSceneSelected = (value: any) => {
  room.value.chatSceneId = value;
  showScenePicker.value = false;
};

// Mode Picker
const showModePicker = ref(false);
const modeOptions = [
  {
    text: t("Dungeons.create.modeOption.1"),
    value: 1,
  },
];
const onModeSelected = (value: any) => {
  room.value.mode = value;
  showModePicker.value = false;
};

// Max Mate Picker
const showMaxMatePicker = ref(false);
const maxMateOptions = [
  {
    text: "2",
    value: 2,
  },
  {
    text: "3",
    value: 3,
  },
  {
    text: "4",
    value: 4,
  },
];
const onMaxMateSelected = (value: any) => {
  room.value.maxPlayers = value;
  showMaxMatePicker.value = false;
};

// Duration Picker
const showDurationPicker = ref(false);
const durationOptions = [
  {
    text: "10",
    value: 10,
  },
  {
    text: "20",
    value: 20,
  },
  {
    text: "30",
    value: 30,
  },
];
const onDurationSelected = (value: any) => {
  room.value.duration = value;
  showDurationPicker.value = false;
};

// Start Date Picker
const showStartDatePicker = ref(false);
const startDate = ref([now.getFullYear(), now.getMonth() + 1, now.getDate()]);
const onStartDateSelected = (value: any) => {
  const [year, month, day] = value.selectedValues;
  const origin = new Date(room.value.dtStart);

  room.value.dtStart = new Date(
    year,
    month - 1,
    day,
    origin.getHours(),
    origin.getMinutes()
  ).toISOString();

  showStartDatePicker.value = false;
};

// Start Time Picker
const showStartTimePicker = ref(false);
const startTime = ref([now.getHours(), now.getMinutes()]);
const onStartTimeSelected = (value: any) => {
  const [hours, minutes] = value.selectedValues;
  const origin = new Date(room.value.dtStart);

  room.value.dtStart = new Date(
    origin.getFullYear(),
    origin.getMonth(),
    origin.getDate(),
    hours,
    minutes
  ).toISOString();

  showStartTimePicker.value = false;
};

const clickCreateRoom = async () => {
  const loading = showLoadingToast({
    message: t("Message.creating"),
  });

  try {
    await createScriptGameRoom({
      ...room.value,
      mateId: mateStore.currentMate?.id!,
    });

    loading.close();

    showSuccessToast({
      message: t("Message.creationSuccessful"),
    });

    setTimeout(() => {
      router.back();
    }, 1000);
  } catch (e) {
    loading.close();
    showFailToast(t("Message.creationFailed"));
  }
};
</script>

<style scoped>
.status-1,
.status-2 {
  background: #fed500;
}

.status-3 {
  background: #00fe43;
}
</style>
